import { Box, Tooltip } from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { DragEvent, KeyboardEvent, MouseEvent, RefCallback } from 'react'

import type { BarColors } from '@/features/bidding/components/cascading/bid-ladder/shared/getBarColors'

type BarSegmentProps = {
  uuid: string
  code: string
  regulationObjectCode: string
  capacityMW: number
  segmentWidthPercent: number
  isHighlighted: boolean
  colors: BarColors
  groupKey: string
  /** Whether this segment is draggable */
  draggable?: boolean
  /** Ref callback for focus management */
  refCallback: RefCallback<HTMLDivElement>
  onContextMenu: (event: MouseEvent) => void
  onMouseEnter: () => void
  onMouseLeave: () => void
  onKeyDown: (event: KeyboardEvent) => void
  onDragStart?: (event: DragEvent) => void
  onDragEnd?: () => void
}

function BarSegment({
  code,
  regulationObjectCode,
  capacityMW,
  segmentWidthPercent,
  isHighlighted,
  colors,
  draggable = false,
  refCallback,
  onContextMenu,
  onMouseEnter,
  onMouseLeave,
  onKeyDown,
  onDragStart,
  onDragEnd,
}: Readonly<BarSegmentProps>) {
  const { barColor, barColorDark, barColorMedium } = colors

  return (
    <Tooltip arrow placement="top" title={regulationObjectCode}>
      <Box
        ref={refCallback}
        aria-label={`${code}, ${capacityMW.toFixed(1)} megawatts`}
        draggable={draggable}
        role="button"
        sx={{
          width: `${segmentWidthPercent}%`,
          minWidth: 12,
          height: '100%',
          backgroundColor: isHighlighted ? barColorMedium : barColor,
          borderRadius: '3px',
          cursor: draggable ? 'grab' : 'default',
          transition: 'all 0.15s ease',
          boxShadow: isHighlighted ? `inset 0 0 0 2px ${barColorDark}` : 'none',
          zIndex: isHighlighted ? 1 : 0,
          userSelect: 'none',
          touchAction: 'none',
          '@media (hover: hover)': {
            '&:hover': {
              backgroundColor: barColorMedium,
              transform: draggable ? 'scale(1.02)' : 'none',
            },
          },
          '&:active': draggable
            ? {
                cursor: 'grabbing',
                transform: 'scale(0.98)',
                opacity: 0.8,
              }
            : {},
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${barColorDark}, 0 0 0 4px ${alpha(barColorDark, 0.2)}`,
            zIndex: 2,
          },
        }}
        tabIndex={0}
        onContextMenu={onContextMenu}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        onKeyDown={onKeyDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    </Tooltip>
  )
}

export default BarSegment
